import React, { useEffect, useRef, useState } from "react";
import { usePlan } from "../context/PlanContext";
import { Bot, X, Send, Sparkles, Lock } from "lucide-react";

/**
 * Floating AI Trading Assistant panel — Premium+.
 * Uses SSE via fetch() to stream tokens from /api/ai/chat.
 * Not shown for Free plan (unless the button opens the upgrade modal).
 */
export default function AIAssistant({ tickerContext }) {
  const { plan, showUpgrade } = usePlan();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `ai_${Math.random().toString(36).slice(2, 12)}`);
  const listRef = useRef(null);
  const gated = plan === "free";

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const openPanel = () => {
    if (gated) { showUpgrade("ai_assistant"); return; }
    setOpen(true);
  };

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setInput("");
    setSending(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`;
      const resp = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, session_id: sessionId, ticker_context: tickerContext }),
      });
      if (!resp.ok || !resp.body) {
        const t = await resp.text().catch(() => "");
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: `Error: ${t || resp.status}` };
          return copy;
        });
        setSending(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Parse SSE frames
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          if (part.startsWith("event: done")) break;
          const dataLine = part.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          const chunk = dataLine.slice(6);
          if (chunk === "[DONE]") break;
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, content: (last.content || "") + chunk };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: `Error: ${e.message}` };
        return copy;
      });
    } finally { setSending(false); }
  };

  return (
    <>
      <button
        onClick={openPanel}
        className="fixed bottom-6 right-6 z-40 rounded-full w-12 h-12 flex items-center justify-center bg-[var(--bull)] text-black shadow-lg hover:scale-110 transition-transform"
        aria-label="Open AI Trading Assistant"
        data-testid="ai-assistant-toggle"
      >
        {gated ? <Lock className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-6" data-testid="ai-assistant-panel">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative w-full sm:w-[420px] h-[80vh] sm:h-[600px] card-surface flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[var(--bull)]/15 border border-[var(--bull)]/30 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-[var(--bull)]" />
                </div>
                <div>
                  <div className="text-white text-[14px] font-semibold tracking-tight">FlowFinder AI</div>
                  <div className="label-mono">Trading Assistant · Premium</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white p-1"
                      data-testid="ai-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-white/50 text-[13px] text-center py-8">
                  <Sparkles className="w-4 h-4 mx-auto mb-2 text-[var(--bull)]" />
                  Ask about setups, indicators, or how to build a strategy.
                  <div className="mt-3 text-[11px] label-mono">Try: "Explain RSI divergence on AAPL"</div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={`msg-${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[var(--bull)]/15 border border-[var(--bull)]/30 text-white"
                      : "bg-white/[0.04] border border-white/10 text-white/90"
                  }`}>
                    {m.content || (sending && m.role === "assistant" ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-white/5 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                placeholder="Ask FlowFinder AI…"
                disabled={sending}
                className="input-glass flex-1"
                data-testid="ai-input"
              />
              <button onClick={send} disabled={sending || !input.trim()} className="btn-accent p-2.5"
                      data-testid="ai-send-btn">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
